import { fetchAuthSession } from 'aws-amplify/auth';

export interface ApiError {
  code: string;
  message: string;
  details?: any;
  retryable?: boolean;
}

interface ApiResponse<T = any> {
  data?: T;
  error?: ApiError;
}

interface RequestConfig {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE';
  headers?: Record<string, string>;
  body?: any;
  requireAuth?: boolean;
  timeout?: number;
  retryConfig?: {
    maxRetries?: number;
    retryDelay?: number;
    backoffMultiplier?: number;
  };
}

class ApiClient {
  private baseUrl: string;

  constructor() {
    this.baseUrl = process.env.REACT_APP_API_ENDPOINT || '';
  }

  private async getAuthHeaders(): Promise<Record<string, string>> {
    try {
      const session = await fetchAuthSession();
      const token = session.tokens?.idToken?.toString();
      
      if (!token) {
        throw new Error('No access token available');
      }

      return {
        'Authorization': `Bearer ${token}`,
      };
    } catch (error) {
      console.error('Failed to get auth headers:', error);
      throw new Error('Authentication required');
    }
  }

  private async makeRequest<T>(
    endpoint: string, 
    config: RequestConfig = {}
  ): Promise<ApiResponse<T>> {
    const {
      method = 'GET',
      headers = {},
      body,
      requireAuth = true,
      timeout = 30000, // 30 seconds default timeout
    } = config;

    try {
      const url = `${this.baseUrl}${endpoint}`;
      
      const requestHeaders: Record<string, string> = {
        'Content-Type': 'application/json',
        ...headers,
      };

      // Add authentication headers if required
      if (requireAuth) {
        const authHeaders = await this.getAuthHeaders();
        Object.assign(requestHeaders, authHeaders);
      }

      const requestConfig: RequestInit = {
        method,
        headers: requestHeaders,
        signal: AbortSignal.timeout(timeout),
      };

      if (body && method !== 'GET') {
        requestConfig.body = JSON.stringify(body);
      }

      const response = await fetch(url, requestConfig);
      
      // Handle different response types
      let responseData;
      const contentType = response.headers.get('content-type');
      
      if (contentType && contentType.includes('application/json')) {
        responseData = await response.json();
      } else {
        responseData = await response.text();
      }

      if (!response.ok) {
        // Handle API error responses
        const error: ApiError = responseData?.error || {
          code: `HTTP_${response.status}`,
          message: responseData?.message || response.statusText || 'Request failed',
          retryable: this.isRetryableStatusCode(response.status),
        };

        return { error };
      }

      // Unwrap our API envelope: { success, data: {...}, message, timestamp }
      const unwrapped = (responseData && typeof responseData === 'object' && 'success' in responseData)
        ? responseData.data
        : responseData;

      return { data: unwrapped };
    } catch (error: any) {
      console.error('API request failed:', error);
      
      // Handle timeout errors
      if (error.name === 'TimeoutError' || error.name === 'AbortError') {
        return {
          error: {
            code: 'TIMEOUT_ERROR',
            message: 'The request took too long to complete. Please try again.',
            retryable: true,
          }
        };
      }

      // Handle network errors
      if (error.name === 'TypeError' && (
        error.message.includes('fetch') || 
        error.message.includes('network') ||
        error.message.includes('Failed to fetch')
      )) {
        return {
          error: {
            code: 'NETWORK_ERROR',
            message: 'Unable to connect to the server. Please check your internet connection.',
            retryable: true,
          }
        };
      }

      // Handle authentication errors
      if (error.message === 'Authentication required') {
        return {
          error: {
            code: 'AUTH_REQUIRED',
            message: 'Please sign in to continue.',
            retryable: false,
          }
        };
      }

      return {
        error: {
          code: 'UNKNOWN_ERROR',
          message: error.message || 'An unexpected error occurred',
          retryable: false,
        }
      };
    }
  }

  private isRetryableStatusCode(status: number): boolean {
    // Retry on server errors and some client errors
    return status >= 500 || status === 408 || status === 429;
  }

  // Gear API methods
  async getGear(): Promise<ApiResponse<any[]>> {
    const r = await this.makeRequest<any>('/gear/');
    if (r.error) return r;
    return { data: r.data?.gear ?? [] };
  }

  async createGear(gear: any): Promise<ApiResponse<any>> {
    const r = await this.makeRequest<any>('/gear/', { method: 'POST', body: gear });
    if (r.error) return r;
    return { data: r.data?.gear };
  }

  async updateGear(id: string, gear: any): Promise<ApiResponse<any>> {
    const r = await this.makeRequest<any>(`/gear/${id}`, { method: 'PUT', body: gear });
    if (r.error) return r;
    return { data: r.data?.gear };
  }

  async deleteGear(id: string): Promise<ApiResponse<void>> {
    return this.makeRequest(`/gear/${id}`, { method: 'DELETE' });
  }

  // Template API methods
  async getTemplates(): Promise<ApiResponse<any[]>> {
    const r = await this.makeRequest<any>('/templates/');
    if (r.error) return r;
    return { data: r.data?.templates ?? [] };
  }

  async createTemplate(template: any): Promise<ApiResponse<any>> {
    const r = await this.makeRequest<any>('/templates/', { method: 'POST', body: template });
    if (r.error) return r;
    return { data: r.data?.template };
  }

  async updateTemplate(id: string, template: any): Promise<ApiResponse<any>> {
    const r = await this.makeRequest<any>(`/templates/${id}`, { method: 'PUT', body: template });
    if (r.error) return r;
    return { data: r.data?.template };
  }

  async deleteTemplate(id: string): Promise<ApiResponse<void>> {
    return this.makeRequest(`/templates/${id}`, { method: 'DELETE' });
  }

  async duplicateTemplate(id: string): Promise<ApiResponse<any>> {
    const r = await this.makeRequest<any>(`/templates/${id}/duplicate`, { method: 'POST' });
    if (r.error) return r;
    return { data: r.data?.template };
  }

  // Trip API methods
  async getTrips(): Promise<ApiResponse<any[]>> {
    const r = await this.makeRequest<any>('/trips/');
    if (r.error) return r;
    return { data: r.data?.trips ?? [] };
  }

  async createTrip(trip: any): Promise<ApiResponse<any>> {
    const r = await this.makeRequest<any>('/trips/', { method: 'POST', body: trip });
    if (r.error) return r;
    return { data: r.data?.trip };
  }

  async updateTrip(id: string, trip: any): Promise<ApiResponse<any>> {
    const r = await this.makeRequest<any>(`/trips/${id}`, { method: 'PUT', body: trip });
    if (r.error) return r;
    return { data: r.data?.trip };
  }

  async deleteTrip(id: string): Promise<ApiResponse<void>> {
    return this.makeRequest(`/trips/${id}`, { method: 'DELETE' });
  }

  async updateTripItemStatus(tripId: string, itemId: string, packed: boolean): Promise<ApiResponse<any>> {
    const r = await this.makeRequest<any>(`/trips/${tripId}/items/${itemId}/packed`, { method: 'PUT', body: { packed } });
    if (r.error) return r;
    return { data: r.data?.trip };
  }

  async getTripProgress(id: string): Promise<ApiResponse<any>> {
    const r = await this.makeRequest<any>(`/trips/${id}/progress`);
    if (r.error) return r;
    return { data: r.data?.progress };
  }

  // User API methods
  async getUserProfile(): Promise<ApiResponse<any>> {
    const r = await this.makeRequest<any>('/auth/user');
    if (r.error) return r;
    return { data: r.data?.user ?? r.data };
  }

  async updateUserProfile(data: { name?: string; onboarding_dismissed?: boolean }): Promise<ApiResponse<any>> {
    const r = await this.makeRequest<any>('/auth/user', { method: 'PUT', body: data });
    if (r.error) return r;
    return { data: r.data?.user ?? r.data };
  }

  async seedSampleData(): Promise<ApiResponse<{ gear_created: number; templates_created: number }>> {
    const r = await this.makeRequest<any>('/auth/seed-data', { method: 'POST' });
    if (r.error) return r;
    return { data: r.data };
  }
}

// Export a singleton instance
export const apiClient = new ApiClient();
export default apiClient;
